/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useCallback } from 'react';

import { ApiClientError } from '../types/api/ApiClientError';
import { UnprocessableEntityPayload } from '../types/api/UnprocessableEntityPayload';
import { useObjectState } from './useObjectState';

type ObjectMap = { [key: string]: any };

export type ApiValidationErrors<Values extends ObjectMap = ObjectMap> =
  | {
      [K in keyof Values]: string;
    }
  | { [key: string]: string };

export type ApiValidationGetFieldError<Values extends ObjectMap = ObjectMap> = (field: keyof Values & string) => string;
export type ApiValidationResetFieldError<Values extends ObjectMap = ObjectMap> = (field: keyof Values & string) => any;
export type ApiValidationHasFieldError<Values extends ObjectMap = ObjectMap> = (
  field: keyof Values & string,
) => boolean;
type ApiValidationSetError<Values extends ObjectMap = ObjectMap> = (
  error: ApiClientError,
) => ApiValidationState<Values>;
type ApiValidationGetStateFromError<Values extends ObjectMap = ObjectMap> = (
  error: ApiClientError,
) => ApiValidationState<Values>;

export type ApiValidationResult<Values extends ObjectMap = ObjectMap> = {
  apiErrors: ApiValidationErrors<Values>;
  errorMessage?: string;
  error?: string;
  hasFieldApiError: ApiValidationHasFieldError<Values>;
  getFieldApiError: ApiValidationGetFieldError<Values>;
  resetFieldApiError: ApiValidationResetFieldError<Values>;
  setApiError: ApiValidationSetError;
  resetApiError: () => any;
  getStateFromError: ApiValidationGetStateFromError<Values>;
};

type ErrorType = 'form_field' | 'common' | 'unexpected' | 'none';

export type ApiValidationState<Values extends ObjectMap = ObjectMap> = {
  apiErrors: ApiValidationErrors<Values>;
  errorMessage?: string;
  error?: string;
  errorType: ErrorType;
};

const processApiError = <Values extends ObjectMap = ObjectMap>(error: ApiClientError): ApiValidationState<Values> => {
  const response = error.response;
  if (!response) {
    return {
      apiErrors: {},
      errorMessage: 'unexpected_error',
      error: 'unexpected_error',
      errorType: 'unexpected',
    };
  }
  const message = response.data.message;
  if (message === 'unprocessable_entity') {
    const apiErrors: ApiValidationErrors<any> = {};
    const payload = response.data.payload as UnprocessableEntityPayload;
    payload.map(i => (apiErrors[i.property] = i.constraints[0] || ''));
    return {
      apiErrors,
      errorMessage: undefined,
      error: message,
      errorType: 'form_field',
    };
  } else if (message === 'duplicate_email' || message === 'duplicate_name' || message === 'duplicate_identifier') {
    const fieldName = message.split('_').slice(1).join('_');
    return {
      apiErrors: { [fieldName]: message },
      errorMessage: undefined,
      error: message,
      errorType: 'form_field',
    };
  } else if (message === 'invalid_credentials' || message === 'invalid_language') {
    return {
      apiErrors: {},
      errorMessage: message,
      error: message,
      errorType: 'common',
    };
  } else {
    return {
      apiErrors: {},
      errorMessage: message,
      error: message,
      errorType: 'common',
    };
  }
};

const initialValues: ApiValidationState = {
  errorMessage: undefined,
  error: undefined,
  apiErrors: {},
  errorType: 'none',
};

export const useApiValidation = <Values extends ObjectMap = ObjectMap>(
  error?: ApiClientError,
): ApiValidationResult<Values> => {
  const [state, setState] = useObjectState<ApiValidationState<Values>>(() =>
    error ? processApiError<Values>(error) : { ...initialValues },
  );

  const hasFieldApiError: ApiValidationHasFieldError<Values> = useCallback(
    field => !!state.apiErrors[field],
    [state.apiErrors],
  );
  const getFieldApiError: ApiValidationGetFieldError<Values> = useCallback(
    field => state.apiErrors[field] || '',
    [state.apiErrors],
  );

  const resetFieldApiError: ApiValidationResetFieldError<Values> = useCallback(
    field => {
      if (state.apiErrors[field]) {
        const nextErrors = { ...state.apiErrors };
        delete nextErrors[field];
        setState({ apiErrors: nextErrors });
      }
    },
    [setState, state.apiErrors],
  );

  const getStateFromError: ApiValidationGetStateFromError<Values> = useCallback(error => processApiError(error), []);

  const setApiError: ApiValidationSetError = useCallback(
    error => {
      const s = getStateFromError(error);
      setState(s);
      return s;
    },
    [getStateFromError, setState],
  );

  const resetApiError = useCallback(() => setState({ ...initialValues }), [setState]);

  return {
    ...state,
    hasFieldApiError,
    getFieldApiError,
    resetFieldApiError,
    setApiError,
    resetApiError,
    getStateFromError,
  };
};

export { processApiError };
